import { useState, type ReactNode } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  useTransitionStyles,
  FloatingPortal,
  type Placement,
} from "@floating-ui/react";
import { cn } from "@/lib/utils";

interface FloatingTooltipProps {
  trigger: ReactNode;
  children: ReactNode;
  placement?: Placement;
  disabledTooltip?: boolean;
  className?: string;
  usePortal?: boolean;
  strategy?: "absolute" | "fixed";
  contentClassName?: string;
}

export function FloatingTooltip({
  trigger,
  children,
  placement = "top",
  disabledTooltip = false,
  className,
  usePortal = true,
  strategy = "absolute",
  contentClassName,
}: FloatingTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    strategy,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ["top", "bottom", "left", "right"],
        fallbackStrategy: "bestFit",
        crossAxis: true,
      }),
      shift({ padding: 5 }),
    ],
  });

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
    },
    common: {
      transitionProperty: "opacity",
      transitionDuration: "200ms",
      transitionTimingFunction: "ease-in-out",
    },
  });

  const content = (
    <div
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        ...styles,
      }}
      className={cn(
        "z-100 rounded-md bg-white px-3 py-2 text-sm shadow-md dark:bg-gray-800",
        "overflow-hidden border",
        "bg-popover dark:bg-popover",
        "shadow-lg",
        "px-3 py-1.5 text-sm text-popover-foreground/90",
        "max-w-[280px]",
        contentClassName,
      )}
    >
      {children}
    </div>
  );

  if (disabledTooltip === true) return trigger;

  return (
    <>
      <div
        ref={refs.setReference}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        className={cn("flex", className)}
      >
        {trigger}
      </div>

      {usePortal ? <FloatingPortal>{isMounted && content}</FloatingPortal> : isMounted && content}
    </>
  );
}
